<div class="module-list-container">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <div class="breadcrumb">
        <mat-icon>home</mat-icon>
        <span>Dashboard</span>
        <mat-icon>chevron_right</mat-icon>
        <span>Modules</span>
      </div>
      <h1>Module Management</h1>
      <p class="header-subtitle">Configure and manage system modules and features</p>
    </div>
    <button mat-raised-button class="btn-primary btn-with-icon" (click)="openCreateDialog()">
      <mat-icon>add</mat-icon>
      <span>Add Module</span>
    </button>
  </div>

  <!-- Search and Filters Section -->
  <div class="search-section">
    <div class="search-controls">
      <div class="col-6">
        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Search modules</mat-label>
          <input matInput
                 [(ngModel)]="searchTerm"
                 (input)="filterModules()"
                 placeholder="Search by name, description, or status...">
          <mat-icon matPrefix>search</mat-icon>
          @if (searchTerm) {
            <button matSuffix mat-icon-button (click)="clearSearch()">
              <mat-icon>close</mat-icon>
            </button>
          }
        </mat-form-field>
      </div>

      <div class="col-6">
        <mat-form-field appearance="outline" class="type-filter-field">
          <mat-label>Filter by Type</mat-label>
          <mat-select [(ngModel)]="selectedType" (selectionChange)="onTypeChange()">
            <mat-option value="">All Types</mat-option>
            @for (type of moduleTypes; track type) {
              <mat-option [value]="type">
                {{ type }}
              </mat-option>
            }
          </mat-select>
          <mat-icon matPrefix>category</mat-icon>
        </mat-form-field>
      </div>
    </div>
    
    <div class="filter-stats">
      <span class="stats-badge">
        <mat-icon>apps</mat-icon>
        <strong>{{ filteredModules.length }}</strong> Module{{ filteredModules.length !== 1 ? 's' : '' }}
      </span>
      <span class="stats-badge active">
        <mat-icon>check_circle</mat-icon>
        <strong>{{ getActiveModuleCount() }}</strong> Active
      </span>
      <span class="stats-badge core">
        <mat-icon>star</mat-icon>
        <strong>{{ getCoreModuleCount() }}</strong> Core
      </span>
    </div>
  </div>

  <!-- Module Grid -->
  <div class="content-section">
    @if (filteredModules.length > 0) {
      <div class="modules-grid">
        @for (module of paginatedModules; track module.id) {
          <mat-card class="module-card" [class.inactive-card]="module.status !== 'active'">
            <!-- Card Header -->
            <div class="card-header">
              <div class="module-avatar-wrapper">
                <div mat-card-avatar class="module-avatar" 
                     [class]="getModuleTypeClass(module.type)"
                     [style.background]="module.color ? getModuleGradient(module.color) : null">
                  <mat-icon>{{ module.icon || 'apps' }}</mat-icon>
                </div>
                @if (module.isCore) {
                  <div class="core-badge">
                    <mat-icon>star</mat-icon>
                  </div>
                }
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content" (click)="openViewDialog(module)">
              <div class="module-title">
                <h3 class="module-name">{{ module.name }}</h3>
                <span class="module-slug">{{ module.slug }}</span>
              </div>

              @if (module.description) {
                <p class="module-description">{{ module.description }}</p>
              } @else {
                <p class="module-description no-description">No description provided</p>
              }

              <div class="module-meta">
                <div class="meta-item">
                  <mat-icon>category</mat-icon>
                  <span>{{ module.type || 'Other' }}</span>
                </div>
                @if (module.version) {
                  <div class="meta-item">
                    <mat-icon>label</mat-icon>
                    <span>v{{ module.version }}</span>
                  </div>
                }
                <div class="meta-item status" [class.active]="module.status === 'active'" [class.inactive]="module.status !== 'active'">
                  <mat-icon>{{ module.status === 'active' ? 'check_circle' : 'cancel' }}</mat-icon>
                  <span>{{ module.status | titlecase }}</span>
                </div>
              </div>

              @if (module.features && module.features.length > 0) {
                <div class="module-features">
                  <div class="features-label">
                    <mat-icon>stars</mat-icon>
                    <span>Features</span>
                  </div>
                  <div class="features-list">
                    @for (feature of module.features.slice(0, 3); track feature) {
                      <span class="feature-tag">{{ feature }}</span>
                    }
                    @if (module.features.length > 3) {
                      <span class="feature-tag more">+{{ module.features.length - 3 }} more</span>
                    }
                  </div>
                </div>
              }
            </div>

            <!-- Card Actions -->
            <div class="card-actions">
              <button mat-button class="action-btn view-btn" (click)="openViewDialog(module)">
                <mat-icon>visibility</mat-icon>
                <span>View</span>
              </button>
              <button mat-icon-button class="action-icon-btn edit-btn" (click)="openEditDialog(module)" matTooltip="Edit">
                <mat-icon>edit</mat-icon>
              </button>
              <button mat-icon-button class="action-icon-btn delete-btn" (click)="deleteModule(module)" matTooltip="Delete">
                <mat-icon>delete</mat-icon>
              </button>
            </div>
          </mat-card>
        }
      </div>

      <!-- Pagination -->
      <div class="pagination-section">
        <app-pagination
          [totalItems]="totalItems"
          [pageSize]="pageSize"
          [currentPage]="currentPage"
          [pageSizeOptions]="pageSizeOptions"
          [showPageSizeSelector]="true"
          [showPageInfo]="true"
          [showFirstLastButtons]="true"
          (pageChange)="onPageChange($event)">
        </app-pagination>
      </div>
    } @else {
      <!-- Empty State -->
      <div class="empty-state">
        <div class="empty-icon-wrapper">
          <mat-icon class="empty-icon">apps</mat-icon>
        </div>
        <h3 class="empty-title">No Modules Found</h3>
        @if (searchTerm || selectedType) {
          <p class="empty-message">No modules match your current filters. Try adjusting your search or filter criteria.</p>
          <button mat-raised-button class="btn-secondary" (click)="clearFilters()">
            <mat-icon>clear</mat-icon>
            Clear Filters
          </button>
        } @else {
          <p class="empty-message">Get started by creating your first module to extend system functionality</p>
          <button mat-raised-button class="btn-primary btn-with-icon" (click)="openCreateDialog()">
            <mat-icon>add</mat-icon>
            <span>Add First Module</span>
          </button>
        }
      </div>
    }
  </div>
</div>
